/**
 * MadebbProtectedRoute
 * Guards /madebb/* routes. Redirects unauthenticated users to /madebb/login
 * instead of showing the offshore-sails LoginForm.
 *
 * MADE-BUG-07
 * MADE-018: Added requireStaff prop for staff-only routes (ManufacturingHub).
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

/** Staff domain/email allowlist — mirrors middleware/firebaseAuth.js */
export const STAFF_DOMAINS = ['doylecaribbean.com', 'made.bb', 'offshore-sails.com'];
export const STAFF_OVERRIDE_EMAILS = ['skipsavoie@gmail.com', 'msavoie@gmail.com', 'trim@findsanctuary.ai'];

export function isStaffUser(email: string | null | undefined): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  if (STAFF_OVERRIDE_EMAILS.includes(lower)) return true;
  const domain = lower.split('@')[1];
  return domain ? STAFF_DOMAINS.includes(domain) : false;
}

interface MadebbProtectedRouteProps {
  children: React.ReactNode;
  /** When true, also enforces staff membership (staff domain or override email).
   *  Non-staff authenticated users are redirected to /madebb/login. */
  requireStaff?: boolean;
}

export const MadebbProtectedRoute: React.FC<MadebbProtectedRouteProps> = ({
  children,
  requireStaff = false,
}) => {
  const { user, loading } = useAuth();

  // LOCAL E2E BYPASS: skip Firebase auth checks when running local E2E tests.
  // VITE_E2E_TEST_MODE is set only by the Playwright webServer command; never in production.
  if (import.meta.env.VITE_E2E_TEST_MODE === 'true') {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        gap={2}
      >
        <CircularProgress size={50} />
        <Typography variant="h6" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/madebb/login" replace />;
  }

  if (requireStaff && !isStaffUser(user.email)) {
    return <Navigate to="/madebb/login" replace />;
  }

  return <>{children}</>;
};
